import { formatDate } from '@ynap/intl-formatters';
import { pdpSelectors } from '@ynap/product-details-state';
import { constructUrl } from '@ynap/url-configuration';
import { productSelectors } from '@ynap/product-state';

const { getSelectedProductImages } = pdpSelectors;
const { getGenderFromAttributes } = productSelectors;

const EmptyMasterCategory = {
    label: '',
    child: { label: '', child: { label: '' } },
};
export default function getLineItem({ config }, details, quantity, messages, price = {}, locale) {
    const {
        catalogEntryView: product,
        adjustment,
        alreadyInWishlist,
        genericTotal,
        giftListItemID,
        isRemote = false,
        orderItemId,
        partNumber,
        removed,
        salesTax,
        taxByTaxCategory,
        xitem_reservationExpiryDate,
        xitem_local_stock,
    } = details;

    if (product) {
        // TODO: Check whether productColours are always present. Docs are ambigious, having it marked as mandatory in one place but not in another
        // https://confluence.nap/pages/viewpage.action?pageId=50762661&focusedCommentId=239895802#comment-239895802
        // catalogueEntryView of cart response returns 1 productColur per orderItem
        const {
            name,
            designerName,
            designerNameEN,
            designerIdentifier,
            productColours: [productColour] = [],
            masterCategory = EmptyMasterCategory,
            tracking,
            productId,
            designerCartItemLimit,
            firstVisibleDate,
        } = product;
        const isHazardous =
            productColour?.sKUs.find((sKU) => hasAttribute(sKU.attributes, 'Hazardous Materials')) || hasAttribute(product.attributes, 'LithiumBattery');

        const {
            label: primaryCategory,
            labelEN: primaryCategoryEN,
            categoryId: primaryCategoryId,
            child: {
                label: subCategory1,
                labelEN: subCategory1EN,
                categoryId: subCategory1Id,
                child: { label: subCategory2 = '', labelEN: subCategory2EN = '', categoryId: subCategory2Id } = {},
            },
        } = masterCategory;

        const availableImageSizes = (config.head || config).availableImageSizes; // config.availableImageSizes for legacy config
        const imageSphere = (config.head || config).aspectRatio?.imagesphere;

        const productImages = productColour
            ? getSelectedProductImages(product, productColour, availableImageSizes || ['208'], undefined, null, imageSphere)
            : [];
        const [{ url: image } = {}] = productImages;
        const thumbnail = productColour ? getSelectedProductImages(product, productColour, ['60'], null, null, imageSphere) : [];
        const variantPartNumber = productColour?.partNumber;

        const size = productColour?.sKUs[0].size.labelSize?.includes('--')
            ? messages?.lineItem.oneSize()
            : productColour?.sKUs[0].size?.centralSizeLabel || productColour?.sKUs[0].size?.labelSize;
        // TODO: see ISCC-464, instead of just using this array to detemine whether or not to show the cumulative % discount on a per line itm basis,
        // we should use this, once WCS provides further % values for each adjustment, to really show the discounts itemised for each line item.
        const orderLevelDiscounts = adjustment
            ? adjustment
                  .filter((thisAdjustment) => thisAdjustment.displayLevel === 'Order' && thisAdjustment.usage === 'Discount')
                  .map((thisAdjustment) => thisAdjustment.code)
            : [];

        const reservationId = details['xitem_attr-reservationId'];
        const isReservation = !!reservationId;
        const dateFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        const parsedLocale = locale?.id || locale?.replace('_', '-');
        const productUrlTemplate = config?.global?.services?.urlConfig?.pdp?.construct || '';
        const seoURLKeyword = productColour?.seo?.seoURLKeyword || product?.seo?.seoURLKeyword || '';

        const productUrl = constructUrl(productUrlTemplate, {
            LOCALE: parsedLocale,
            SEO_URLKEYWORD: seoURLKeyword,
        });
        const reservationExpiryDate =
            isReservation && xitem_reservationExpiryDate ? formatDate(new Date(xitem_reservationExpiryDate), dateFormatOptions, parsedLocale) : null;
        const badges = productColour && getBadges(productColour, quantity.inStock, isReservation);
        const isJIT = xitem_local_stock ? !JSON.parse(xitem_local_stock) : undefined;

        const selectedProductName = productColour?.shortDescription ? productColour.shortDescription : name;

        const onPromo = Array.isArray(price?.promotions) && price?.promotions.length > 0;

        const consideredBadge = product?.attributes?.find((attribute) => {
            return attribute?.identifier === 'CONSIDERED';
        });

        const analyticsAttributes = {
            name: tracking?.name || '',
            sustainabilityFlag: !!consideredBadge,
            category: {
                primaryCategory: primaryCategoryEN || primaryCategory,
                subCategory1: subCategory1EN || subCategory1,
                subCategory2: subCategory2EN || subCategory2,
                ids: [primaryCategoryId, subCategory1Id, subCategory2Id],
            },
            gender: getGenderFromAttributes(product?.attributes),
        };

        return {
            designerCartItemLimit: designerCartItemLimit && Number(designerCartItemLimit),
            partNumber,
            // TODO: Check whether this is actually needed
            productId: variantPartNumber, //productId is only used in analytics and requires variantPartNumber
            orderItemId,
            image,
            imageObject: thumbnail?.[0],
            name: selectedProductName,
            nameEN: name,
            brand: designerNameEN || designerName,
            brandId: designerIdentifier,
            colour: productColour && productColour.label,
            size,
            quantity: quantity.inOrder,
            quantityInStock: quantity.inStock,
            badges,
            giftListItemID,
            alreadyInWishlist,
            orderLevelDiscounts,
            price,
            category: {
                primaryCategory,
                subCategory1,
                subCategory2,
                ids: [primaryCategoryId, subCategory1Id, subCategory2Id],
            },
            onSale: !!productColour?.onSale,
            saleExclusive: !!productColour?.saleExclusive,
            onPromo,
            removed,
            genericTotal,
            isReservation,
            reservationId,
            reservationExpiryDate,
            isHazardous: !!isHazardous,
            finalSale: !!productColour?.sKUs.find((sku) => sku.partNumber === partNumber)?.finalSale,
            isJIT,
            isRemote,
            taxByTaxCategory,
            salestax: salesTax,
            analyticsAttributes,
            consideredBadge,
            productUrl,
            gtin: productId,
            locale: locale,
            imageTemplate: productColour?.imageTemplate,
            imageViews: productColour?.imageViews,
            firstVisibleDate
        };
    } else {
        return {
            removed: false,
        };
    }
}

function filterBadges(badges) {
    return badges ? badges.find((item) => ['MERCHANDISING', 'STOCK'].includes(item.type)) : undefined;
}

export function getSelectedProductBadges(productColour, selectedSku) {
    return {
        selectedProductColorFirstBadge: filterBadges(productColour?.badges),
        selectedProductSizeBadge: filterBadges(selectedSku?.badges),
    };
}

export const hasAttribute = (attributes, identifier) =>
    !!attributes?.some((attribute) => {
        const valueHasYes = attribute.values.find((value) => value.identifier?.trim().toLowerCase() === 'yes');
        return attribute.identifier?.trim().toLowerCase() == identifier.trim().toLowerCase() && valueHasYes;
    });

export function getBadges(productColour, quantityInStock, isReservation) {
    // catalogueEntryView of cart response returns 1 sku per productColur ðŸ¤¡
    const [selectedSku] = productColour.sKUs;
    const { selectedProductColorFirstBadge = {}, selectedProductSizeBadge = {} } = getSelectedProductBadges(productColour, selectedSku);
    const badgeToShow = selectedProductSizeBadge.type ? selectedProductSizeBadge : selectedProductColorFirstBadge;
    const reservationBadge =
        selectedProductSizeBadge.type === 'MERCHANDISING'
            ? selectedProductSizeBadge
            : selectedProductColorFirstBadge.type === 'MERCHANDISING'
              ? selectedProductColorFirstBadge
              : {};
    const finalBadge = isReservation ? [reservationBadge] : quantityInStock >= 1 ? [badgeToShow] : [];

    return finalBadge;
}
